import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Hyperlink, Icon, Stack } from '@openedx/paragon';
import { CheckCircleOutline, OpenInNew } from '@openedx/paragon/icons';
import { LineItem } from 'shared/types/cart';
import { getPriceDetailsFromCart } from 'helpers/priceHelpers';
import {
  AboutCourse,
  AboutProgram,
  OrderAbout,
  OrderSummary,
  StartButton,
} from './components';
import { ThankYouProps } from './types';
import { getCoursesDetails, getPaymentMethod } from './utils';

export const ThankYou = ({ order, program }: ThankYouProps) => {
  const { formatMessage } = useIntl();

  const orderNumber = order.orderNumber!;
  const paymentMethod = getPaymentMethod(order.payments);
  const priceDetails = getPriceDetailsFromCart(order);
  const coursesDetails = getCoursesDetails(
    order.lineItems as Required<LineItem>[],
  );

  const isFreeOrder =
    priceDetails.cartTotal === 0 && order?.payments.length === 0;

  const purchaseText = isFreeOrder
    ? formatMessage({
        id: 'payment.thankyou.purchase.free',
        defaultMessage: 'order',
        description: 'Free purchase message',
      })
    : formatMessage({
        id: 'payment.thankyou.purchase.paid',
        defaultMessage: 'payment',
        description: 'Paid purchase message',
      });

  const receiptButtonText = formatMessage({
    id: 'payment.thankyou.view.receipt',
    defaultMessage: 'View receipt',
    description: 'CTA to view the receipt after successful payment',
  });

  const fulfillmentType = program
    ? formatMessage({
        id: 'payment.thankyou.main.body.entitlement',
        defaultMessage: 'entitlement(s)',
        description: 'Program fulfillment type',
      })
    : formatMessage({
        id: 'payment.thankyou.main.body.enrollment',
        defaultMessage: 'enrollment',
        description: 'Course fulfillment type',
      });

  const purchaseType = program
    ? 'program'
    : order.lineItems[0]?.variant?.attributes?.mode;

  return (
    <>
      <div className="container-fluid py-5 bg-light-200 px-md-5">
        <div className="row justify-content-center">
          <section className="col-12 col-md-8 p-0">
            <div className="d-flex align-items-center">
              <Icon
                src={CheckCircleOutline}
                screenReaderText="Check mark"
                className="mr-2 text-success-400"
              />
              <div className="h-5 text-success-700 mb-0">
                <FormattedMessage
                  id="payment.thankyou.main.subheading"
                  defaultMessage="Your {purchase} is successful."
                  description="Purchase success message"
                  values={{ purchase: purchaseText }}
                />
              </div>
            </div>
            <h1 className="mt-4">
              <FormattedMessage
                id="payment.thankyou.main.heading"
                defaultMessage="Congrats! You're ready to begin learning!"
                description="Encouraging message for learners after successful purchase"
              />
            </h1>
            <p>
              <FormattedMessage
                id="payment.thankyou.main.body"
                defaultMessage="Your {purchase} is completed and we've confirmed your {fulfillmentType}. Below is a summary of your order."
                description="Purchase success message body"
                values={{ fulfillmentType, purchase: purchaseText }}
              />
            </p>
            <Stack gap={2} direction="horizontal">
              <StartButton
                program={program}
                courseURL={coursesDetails[0].url}
              />
              {!isFreeOrder && (
                <Button
                  iconAfter={OpenInNew}
                  variant="outline-brand"
                  target="_blank"
                  as="a"
                  href={`${
                    process.env.NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL! +
                    process.env.NEXT_PUBLIC_EDX_CC_ORDER_RECEIPT_PATH!
                  }?order_number=${orderNumber}`}
                >
                  {receiptButtonText}
                </Button>
              )}
            </Stack>
          </section>
        </div>
      </div>
      <div className="container-fluid px-md-5 py-4">
        <div className="row justify-content-center">
          <OrderSummary
            orderNumber={orderNumber}
            priceDetails={priceDetails}
            coursesDetails={coursesDetails}
            paymentMethod={paymentMethod}
            isFreeOrder={isFreeOrder}
          />
        </div>
        <div className="row justify-content-center py-4">
          <section className="col-12 col-md-8 bg-light-500 rounded py-3">
            {program ? (
              <AboutProgram program={program} />
            ) : (
              <AboutCourse {...coursesDetails[0]} />
            )}
          </section>
        </div>
        <div className="row justify-content-center py-4">
          <section className="col-12 col-md-8 rounded border py-3">
            <OrderAbout purchaseType={purchaseType} />
          </section>
        </div>
        <div className="row justify-content-center py-4">
          <section className="col-12 col-md-8">
            <Hyperlink
              destination={process.env.NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL!}
            >
              <FormattedMessage
                id="payment.checkout.order.about"
                defaultMessage="Go to dashboard"
                description="CTA to go to the dashboard after successful purchase"
              />
            </Hyperlink>
          </section>
        </div>
      </div>
    </>
  );
};
